import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Alert,
  Link,
  Grid,
  TablePagination,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { useCurrency } from '../../context/CurrencyContext';
import LatePenaltyPanel from '../LatePenaltyPanel';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import ListViewControls from '../ListViewControls';
import TenantPicker from '../TenantPicker';
import { cyclePeriodFor } from '../../utils/rentCycle';


// Mirrors backend/utils/accountCategories.js.
const ACCOUNT_CATEGORY_LABELS = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', other: 'Other' };

// Used only if /charge-types cannot be reached. These are the codes every existing payment
// already carries, so the form still works rather than offering nothing.
const FALLBACK_TYPES = [
  { value: 'rent', label: 'Rent' },
  { value: 'utility', label: 'Utility' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'other', label: 'Other' }
];

/**
 * A DATEONLY value from the API, rendered as the calendar day it names.
 *
 * Parsed field by field rather than handed to `new Date`. `new Date('2026-08-01')` is UTC
 * midnight, which renders as 31 July for any viewer west of Greenwich — a rental period is
 * a matter of dates, not of instants, and must read the same wherever it is opened.
 */
const formatDay = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return '';
  return format(new Date(y, m - 1, d), 'dd MMM yy');
};

/**
 * Say so when a payment did not cover the months somebody said it covered.
 *
 * The period on the form is what was believed; the server records the months the money
 * actually settled, oldest arrears first, and the receipt it has just drawn says the
 * latter. Both matter on this screen: staff type a period when recording a payment, and a
 * tenant types one when submitting the payment staff then approve. Nobody should hand over
 * a receipt expecting it to say something else.
 */
const warnIfPeriodAdjusted = (payment) => {
  const applied = payment?.periodAdjusted?.applied;
  if (!applied) return;
  toast.warning(
    `Recorded as covering ${formatDay(applied.periodStart)} – ${formatDay(applied.periodEnd)}: the months this payment actually settled, not the period entered.`,
    { autoClose: 12000 }
  );
};

const PaymentManagement = () => {
  // The Type dropdowns are filled from charge_types, not a list in this file. They used to
  // be hardcoded here AND as a database ENUM, so adding one meant a code change and a
  // migration.
  //
  // Falls back to the seeded codes if the call fails: a form that cannot offer a type
  // cannot raise an invoice at all, and the six below are what every existing record
  // already uses.
  const [chargeTypes, setChargeTypes] = useState(FALLBACK_TYPES);

  const fetchChargeTypes = async (usage) => {
    try {
      const res = await axios.get(`/charge-types?usage=${usage}`);
      const rows = (res.data || []).map((t) => ({ value: t.code, label: t.name }));
      if (rows.length) setChargeTypes(rows);
    } catch (e) {
      // Left on the fallback deliberately, and quietly — an error toast on a screen the
      // user opened for something else is noise they cannot act on.
    }
  };

  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState({ status: '', staffComments: '' });

  // new state for create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTenantObj, setSelectedTenantObj] = useState(null);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const { formatMoney: formatCurrency } = useCurrency();
  const [paymentModes, setPaymentModes] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // Which set of records the table is showing, and the date window over it.
  const [view, setView] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPayments, setTotalPayments] = useState(0);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [createPenalty, setCreatePenalty] = useState({ enabled: false, amount: '', reason: '', percent: null });
  const [createForm, setCreateForm] = useState({
    tenant: '',
    invoiceId: '',
    amountPaid: 0,
    periodStart: '',
    periodEnd: '',
    paymentModeId: '',
    receivedIntoAccountId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: 'rent',
    status: 'received',
    staffComments: ''
  });

  useEffect(() => {
    fetchPayments();
    fetchChargeTypes('payment');
    fetchPaymentModes();
    fetchAssetAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A security deposit is one month's rent, held rather than applied. On the form that
  // means: the amount is fixed, there is no invoice to apply it to, no penalty rides on it,
  // and it cannot be recorded until the landlord has said who holds it — the server refuses
  // otherwise, so the form says so first.
  const isSecurity = createForm.paymentType === 'security';
  const monthlyRentOf = (tenant) => parseFloat(tenant?.unit?.monthlyRent || tenant?.property?.monthlyRent || 0) || 0;
  const depositHolder = selectedTenantObj?.property?.landlord?.securityHeldBy || null;
  const depositBlocked = isSecurity && !!selectedTenantObj && !depositHolder;

  const fetchPaymentModes = async () => {
    try {
      const response = await axios.get('/payment-modes?isActive=true');
      setPaymentModes(response.data || []);
    } catch (error) {
      setPaymentModes([]);
    }
  };

  // Offered so staff can override where a payment landed when it differs from the mode's
  // usual account. Asset accounts only — the server rejects anything else.
  const fetchAssetAccounts = async () => {
    try {
      const response = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setAssetAccounts(all.filter((a) => a.type === 'asset' && a.isActive !== false));
    } catch (error) {
      setAssetAccounts([]);
    }
  };

  const handleTenantSelect = async (tenant) => {
    setSelectedTenantObj(tenant);
    setOutstandingInvoices([]);
    if (!tenant) {
      setCreateForm(prev => ({ ...prev, tenant: '', invoiceId: '', amountPaid: 0 }));
      return;
    }
    const monthlyRent = tenant.unit?.monthlyRent || tenant.property?.monthlyRent || 0;
    // A rent payment opens on the tenant's current rent month — 10th to 9th for someone
    // whose cycle starts on the 10th — so an advance is extended from the right day. Only
    // filled in when the period is still blank, so a period already typed is kept.
    const cycle = cyclePeriodFor(tenant.billingCycleDay);
    setCreateForm(prev => ({
      ...prev,
      tenant: tenant._id,
      invoiceId: '',
      amountPaid: monthlyRent,
      ...(prev.paymentType === 'rent' && !prev.periodStart && !prev.periodEnd
        ? { periodStart: cycle.periodStart, periodEnd: cycle.periodEnd }
        : {})
    }));
    try {
      // Every page, not just the first.
      //
      // /invoices pages at fifty. Asking for one page and filtering it in the browser is
      // the same mistake that made tenants past the fiftieth unselectable — here it would
      // hide a tenant's OLDEST outstanding invoices, which are the ones a payment should
      // usually clear first. A tenant with that many open invoices is unusual, but "unusual"
      // is not "never", and the failure is silent when it happens.
      const invoices = [];
      let page = 1;
      // Bounded so a miscounting server cannot spin this forever.
      for (let guard = 0; guard < 20; guard += 1) {
        const res = await axios.get('/invoices', {
          params: { tenant: tenant._id, status: 'pending,overdue,partial', page, limit: 100 }
        });
        const body = res.data;
        const rows = Array.isArray(body) ? body : (body.data || []);
        invoices.push(...rows);
        const total = Array.isArray(body) ? rows.length : (body.total ?? rows.length);
        if (invoices.length >= total || rows.length === 0) break;
        page += 1;
      }
      setOutstandingInvoices(invoices.filter(inv => inv.balance > 0));
    } catch (e) {
      // non-fatal
    }
  };

  const handleInvoiceSelect = (invoice) => {
    if (!invoice) {
      setCreateForm(prev => ({ ...prev, invoiceId: '' }));
      return;
    }
    setCreateForm(prev => ({ ...prev, invoiceId: invoice._id, amountPaid: invoice.balance }));
  };

  // Server-side search and paging. This endpoint has always paged at fifty, but the screen
  // requested no page and showed no pager — so on an agency with hundreds of payments the
  // rest were invisible with nothing on screen saying so.
  const fetchPayments = async (opts = {}) => {
    const targetPage = opts.page ?? page;
    const targetSize = opts.rowsPerPage ?? rowsPerPage;
    try {
      const response = await axios.get('/payments', {
        params: {
          page: targetPage + 1,
          limit: targetSize,
          ...((opts.search ?? search) ? { search: opts.search ?? search } : {}),
          ...((opts.status ?? statusFilter) ? { status: opts.status ?? statusFilter } : {}),
          ...((opts.paymentType ?? typeFilter) ? { paymentType: opts.paymentType ?? typeFilter } : {}),
          // 'all' is the server's default, so it is left off rather than sent as a no-op.
          ...(((opts.view ?? view) && (opts.view ?? view) !== 'all') ? { view: opts.view ?? view } : {}),
          ...((opts.from ?? dateRange.from) ? { from: opts.from ?? dateRange.from } : {}),
          ...((opts.to ?? dateRange.to) ? { to: opts.to ?? dateRange.to } : {})
        }
      });
      const body = response.data;
      if (Array.isArray(body)) {
        setPayments(body);
        setTotalPayments(body.length);
      } else {
        setPayments(body.data || []);
        setTotalPayments(body.total ?? (body.data || []).length);
      }
    } catch (error) {
      toast.error('Error fetching payments');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchPayments({ page: 0, search });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilter = (changes) => {
    setPage(0);
    fetchPayments({ page: 0, ...changes });
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchPayments({ page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0);
    fetchPayments({ page: 0, rowsPerPage: size });
  };

  // A pending payment never reached the ledger, so it can go. Anything received has
  // journal entries behind it and must be cancelled, which reverses them.
  const handleDelete = async (payment) => {
    // Asks why rather than just whether — the reason is the one thing the Deleted view
    // cannot reconstruct later, and it matters most for the case this exists for: a tenant
    // who says they paid and a capture staff can no longer find.
    const reason = window.prompt(
      `Delete this pending payment of ${formatCurrency(payment.amountPaid)}?\n\n`
      + 'It has not been posted, so nothing is reversed. It stays on record under the '
      + 'Deleted view. Why is it being deleted? (optional)'
    );
    if (reason === null) return;
    try {
      const { data } = await axios.delete(`/payments/${payment._id}`, { data: { reason } });
      toast.success(data.message || 'Payment deleted');
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete this payment');
    }
  };

  const handleCancel = async (payment) => {
    const reason = window.prompt(
      [
        `Cancel payment ${payment.receiptNumber || ''} of ${formatCurrency(payment.amountPaid)}?`,
        '',
        'Every journal entry it produced will be reversed, the invoices it paid will reopen,',
        'and any management fee it earned will be released.',
        '',
        'Reason:'
      ].join('\n')
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Give a reason for cancelling this payment'); return; }
    try {
      const { data } = await axios.post(`/payments/${payment._id}/cancel`, { reason });
      toast.success(data.message);
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not cancel this payment');
    }
  };

  const handleReview = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      status: payment.status,
      staffComments: payment.staffComments || '',
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const { data: reviewed } = await axios.put(`/payments/${selectedPayment._id}`, formData);
      
      if (formData.status === 'received') {
        // Approving is what applies the money: the payment is posted, spread across what
        // the tenant owes, and any month it bought ahead of billing is raised. The receipt
        // is drawn from the result, so the months it names can differ from the ones the
        // tenant stated when they submitted it.
        toast.success('Payment verified — receipt generated and applied to the tenant\'s invoices');
        warnIfPeriodAdjusted(reviewed);
      } else if (formData.status === 'queried') {
        toast.success('Payment queried - tenant will be notified');
      } else {
        toast.success('Payment updated');
      }
      
      setOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating payment');
    }
  };

  const handleViewUploadedReceiptFile = async (payment) => {
    try {
      const res = await axios.get(`/payments/${payment._id}/receipt-file`, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const file = new Blob([res.data], { type: contentType });
      window.open(window.URL.createObjectURL(file), '_blank');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to open the uploaded receipt file');
    }
  };

  const handleViewReceipt = async (payment) => {
    try {
      const url = `/payments/${payment._id}/receipt`;
      const res = await axios.get(url, {
        responseType: 'blob'
      });

      // Check if response is actually a PDF
      if (res.data.type && res.data.type !== 'application/pdf') {
        const text = await res.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.message || 'Unable to view receipt PDF');
        } catch {
          toast.error('Server returned non-PDF response');
        }
        return;
      }

      const file = new Blob([res.data], { type: 'application/pdf' });
      const url2 = window.URL.createObjectURL(file);
      window.open(url2, '_blank');
    } catch (e) {
      let errorMessage = 'Unable to view receipt PDF';
      if (e.response) {
        if (e.response.data instanceof Blob) {
          try {
            const text = await e.response.data.text();
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch {
            errorMessage = `Error ${e.response.status}: ${e.response.statusText}`;
          }
        } else {
          errorMessage = e.response.data?.message || errorMessage;
        }
      } else if (e.message) {
        errorMessage = e.message;
      }
      toast.error(errorMessage);
      console.error('Receipt view error:', e);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    try {
      const url = `/payments/${payment._id}/receipt`;
      const res = await axios.get(url, {
        responseType: 'blob'
      });

      // Check if response is actually a PDF
      if (res.data.type && res.data.type !== 'application/pdf') {
        const text = await res.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.message || 'Unable to download receipt PDF');
        } catch {
          toast.error('Server returned non-PDF response');
        }
        return;
      }

      const file = new Blob([res.data], { type: 'application/pdf' });
      const url2 = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url2;
      link.download = `receipt-${payment.receiptNumber || payment._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url2);
    } catch (e) {
      let errorMessage = 'Unable to download receipt PDF';
      if (e.response) {
        if (e.response.data instanceof Blob) {
          try {
            const text = await e.response.data.text();
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch {
            errorMessage = `Error ${e.response.status}: ${e.response.statusText}`;
          }
        } else {
          errorMessage = e.response.data?.message || errorMessage;
        }
      } else if (e.message) {
        errorMessage = e.message;
      }
      toast.error(errorMessage);
      console.error('Receipt download error:', e);
    }
  };

  // Pending is a payment's draft: nothing has been posted to the ledger and no receipt
  // issued, which is why the server allows only this status to be deleted.

  const handleCreate = async () => {
    try {
      if (!createForm.tenant) { toast.error('Please select a tenant'); return; }
      if (!createForm.amountPaid || createForm.amountPaid <= 0) { toast.error('Please enter a valid amount'); return; }
      if (depositBlocked) {
        toast.error(`${selectedTenantObj.property?.landlord?.name || 'The landlord'} has not said who holds security deposits. Set it on the landlord first.`);
        return;
      }
      if (createForm.paymentType === 'rent') {
        if (!createForm.periodStart || !createForm.periodEnd) {
          toast.error('Please give the period this payment covers, from and to');
          return;
        }
        if (createForm.periodEnd < createForm.periodStart) {
          toast.error('Period end cannot be before period start');
          return;
        }
      }
      // Only enforced when the payment is being recorded as received, matching the server:
      // that is when the journal entry is written and the money has to land somewhere.
      if (createForm.status === 'received' && !createForm.receivedIntoAccountId) {
        toast.error('Please record how the money was received, so it posts to the right account');
        return;
      }
      if (createPenalty.enabled && !String(createPenalty.reason || '').trim()) {
        toast.error('Give a reason for the late payment penalty');
        return;
      }
      const { data: recorded } = await axios.post('/payments', {
        ...createForm,
        // Omitted rather than sent empty. Only rent carries a period, and the fields are
        // hidden for every other type but stay in state as ''.
        periodStart: createForm.periodStart || undefined,
        periodEnd: createForm.periodEnd || undefined,
        ...(createPenalty.enabled
          ? { latePenalty: { amount: parseFloat(createPenalty.amount) || 0, percent: createPenalty.percent, reason: createPenalty.reason } }
          : {})
      });
      toast.success('Payment recorded');
      warnIfPeriodAdjusted(recorded);
      setCreateOpen(false);
      setCreatePenalty({ enabled: false, amount: '', reason: '', percent: null });
      setSelectedTenantObj(null);
      setOutstandingInvoices([]);
      setCreateForm({ tenant: '', invoiceId: '', amountPaid: 0, periodStart: '', periodEnd: '', paymentModeId: '', receivedIntoAccountId: '', paymentDate: new Date().toISOString().split('T')[0], paymentType: 'rent', status: 'received', staffComments: '' });
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to record payment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      received: 'success',
      queried: 'info',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };


  // The selected mode decides which accounts are on offer: pick "Bank" and only the bank
  // accounts appear. The server enforces the same match, so the two cannot drift.
  const selectedMode = paymentModes.find(m => m._id === createForm.paymentModeId) || null;
  const accountsForSelectedMode = selectedMode
    ? assetAccounts.filter(a => a.accountCategory === selectedMode.accountCategory)
    : [];
  const selectedModeCategoryLabel = selectedMode
    ? (ACCOUNT_CATEGORY_LABELS[selectedMode.accountCategory] || selectedMode.accountCategory)
    : '';

  // Payments taken before periods were recorded fall back to the months figure, so
  // historical rows still read sensibly rather than showing a blank.
  const formatPeriod = (payment) => {
    if (payment?.periodStart && payment?.periodEnd) {
      return `${formatDay(payment.periodStart)} – ${formatDay(payment.periodEnd)}`;
    }
    if (payment?.numberOfMonths) return `${payment.numberOfMonths} month(s)`;
    return '-';
  };

  // Cancelled and deleted are different events with parallel records - one reverses a
  // posted payment's entries, the other removes a pending capture that never reached the
  // ledger. Same three questions either way, so the table reads whichever set applies.
  const showAudit = view === 'cancelled' || view === 'deleted';
  const auditLabel = view === 'deleted' ? 'Deleted' : 'Cancelled';
  const auditDate = (p) => (view === 'deleted' ? p.deletedAt : p.cancelledAt);
  const auditWho = (p) => (view === 'deleted' ? p.deletedBy?.name : p.cancelledBy?.name);
  const auditReason = (p) => (view === 'deleted' ? p.deletionReason : p.cancellationReason);

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Payment Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={()=>{ setCreateOpen(true); }}>
          Record Payment
        </Button>
      </Box>
      {/* One table throughout - these switch which set of records it holds. Deleted rows
          are soft-deleted and excluded from every other view, so this is the only way to
          reach them. */}
      <ListViewControls
        view={view}
        onViewChange={(v) => {
          setView(v);
          // Status is the finer filter INSIDE a view; carrying "Received" over into
          // Cancelled would return nothing and read as "no cancelled payments".
          setStatusFilter('');
          setPage(0);
          fetchPayments({ page: 0, view: v, status: '' });
        }}
        from={dateRange.from}
        to={dateRange.to}
        onDateChange={(next) => {
          setDateRange(next);
          setPage(0);
          fetchPayments({ page: 0, ...next });
        }}
        dateLabel="Paid"
      />

      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Receipt number, tenant, comment or reason"
        onReset={() => {
          setSearch(''); setStatusFilter(''); setTypeFilter('');
          setView('all'); setDateRange({ from: '', to: '' });
          applyFilter({ search: '', status: '', paymentType: '', view: 'all', from: '', to: '' });
        }}
        filters={[
          { key: 'status', label: 'Status', value: statusFilter,
            onChange: (v) => { setStatusFilter(v); applyFilter({ status: v }); },
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'received', label: 'Received' },
              { value: 'queried', label: 'Queried' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'cancelled', label: 'Cancelled' }
            ] },
          { key: 'paymentType', label: 'Type', value: typeFilter,
            onChange: (v) => { setTypeFilter(v); applyFilter({ paymentType: v }); },
            options: chargeTypes }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Tenant</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Unallocated</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Rent Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Receipt #</TableCell>
              {/* Only where they mean something - on a live payment all three are empty by
                  definition, and blank columns cost width the rest of the table needs. */}
              {showAudit && <TableCell>{auditLabel} on</TableCell>}
              {showAudit && <TableCell>By</TableCell>}
              {showAudit && <TableCell>Reason</TableCell>}
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment._id}>
                <TableCell className="pin-left">{payment.tenant?.name || 'N/A'}</TableCell>
                <TableCell>{format(new Date(payment.paymentDate), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{formatCurrency(payment.amountPaid)}</TableCell>
                <TableCell>
                  {payment.unallocatedAmount > 0 ? (
                    <Typography variant="body2" color="info.main" fontWeight="bold">
                      {formatCurrency(payment.unallocatedAmount)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={payment.paymentType || 'rent'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatPeriod(payment)}</TableCell>
                <TableCell>
                  <Chip label={payment.status} color={getStatusColor(payment.status)} size="small" />
                </TableCell>
                <TableCell>{payment.receiptNumber || 'N/A'}</TableCell>
                {showAudit && (
                  <TableCell>
                    {auditDate(payment)
                      ? format(new Date(auditDate(payment)), 'd MMM yyyy')
                      : <Typography variant="caption" color="text.secondary">not recorded</Typography>}
                  </TableCell>
                )}
                {showAudit && (
                  <TableCell>
                    {auditWho(payment)
                      || <Typography variant="caption" color="text.secondary">unknown</Typography>}
                  </TableCell>
                )}
                {showAudit && (
                  <TableCell>
                    {auditReason(payment)
                      || <Typography variant="caption" color="text.secondary">none given</Typography>}
                  </TableCell>
                )}
                <TableCell className="pin-right">
                  <Box display="flex" gap={1}>
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewReceipt(payment)}>View</Button>
                    {/* Reviewing a deleted payment would write to a row every other screen
                        treats as gone. Withheld rather than offered and then refused. */}
                    {view !== 'deleted' && (
                      <Button size="small" startIcon={<CheckCircleIcon />} color="success" onClick={() => handleReview(payment)}>Review</Button>
                    )}
                    {payment.receiptNumber && (<Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadReceipt(payment)}>PDF</Button>)}
                    {payment.status === 'pending' && view !== 'deleted' && (
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(payment)}>
                        Delete
                      </Button>
                    )}
                    {payment.status === 'received' && (
                      <Button size="small" color="warning" startIcon={<BlockIcon />} onClick={() => handleCancel(payment)}>
                        Cancel
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={showAudit ? 12 : 9}>
                  <EmptyState title="No payments recorded yet" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={totalPayments}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Create Payment Dialog */}
      <Dialog open={createOpen} onClose={() => {
        setCreateOpen(false);
        setSelectedTenantObj(null);
        setOutstandingInvoices([]);
        setCreateForm({ tenant: '', invoiceId: '', amountPaid: 0, periodStart: '', periodEnd: '', paymentModeId: '', receivedIntoAccountId: '', paymentDate: new Date().toISOString().split('T')[0], paymentType: 'rent', status: 'received', staffComments: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 1 }}>
            <TenantPicker
              valueId={createForm.tenant}
              required
              onChange={(val) => handleTenantSelect(val)}
            />
          </Box>

          {selectedTenantObj && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Property</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedTenantObj.property?.propertyName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Landlord</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedTenantObj.property?.landlord?.name || '—'}
                  </Typography>
                </Grid>
                {selectedTenantObj.unit && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Unit</Typography>
                    <Typography variant="body2">{selectedTenantObj.unit.unitNumber || '—'}</Typography>
                  </Grid>
                )}
                {(selectedTenantObj.unit?.monthlyRent || selectedTenantObj.property?.monthlyRent) && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Monthly Rent</Typography>
                    <Typography variant="body2">{formatCurrency(selectedTenantObj.unit?.monthlyRent || selectedTenantObj.property?.monthlyRent)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {selectedTenantObj && !isSecurity && (
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={outstandingInvoices}
                value={outstandingInvoices.find(inv => inv._id === createForm.invoiceId) || null}
                getOptionLabel={(inv) => `${inv.invoiceNumber} — ${inv.invoiceType} — Balance: ${formatCurrency(inv.balance)} — Due: ${new Date(inv.dueDate).toLocaleDateString()}`}
                onChange={(_, val) => handleInvoiceSelect(val)}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                noOptionsText="No outstanding invoices"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Apply to Invoice (optional — auto-allocates if left blank)"
                    fullWidth
                    helperText={outstandingInvoices.length === 0 ? 'No outstanding invoices for this tenant' : `${outstandingInvoices.length} outstanding invoice(s)`}
                  />
                )}
              />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={createForm.amountPaid}
                disabled={isSecurity}
                onFocus={(e) => e.target.select()}
                onChange={e => setCreateForm({ ...createForm, amountPaid: parseFloat(e.target.value) || 0 })}
                helperText={isSecurity ? "One month's rent, paid in full — fixed" : undefined}
              />
            </Grid>
            <Grid item xs={6}>
              {/* Switching away from Rent clears the period as well as hiding it. Left in
                  state it would be sent with a penalty or other payment that has no period,
                  attaching months to money that never covered any. Switching TO a security
                  deposit fixes the amount at one month's rent and drops any invoice chosen —
                  a deposit is held, not applied. */}
              <TextField fullWidth select label="Payment Type" value={createForm.paymentType} onChange={e => {
                const type = e.target.value;
                setCreateForm({
                  ...createForm,
                  paymentType: type,
                  ...(type === 'rent' ? {} : { periodStart: '', periodEnd: '' }),
                  ...(type === 'security' ? { invoiceId: '', amountPaid: monthlyRentOf(selectedTenantObj) } : {})
                });
              }}>
                {chargeTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {isSecurity && selectedTenantObj && (
              <Grid item xs={12}>
                {depositHolder ? (
                  <Alert severity="info">
                    {depositHolder === 'agency'
                      ? 'Held by the agency: this posts to Tenant Deposits Held and is refunded by the agency at move-out.'
                      : `Paid over to ${selectedTenantObj.property?.landlord?.name || 'the landlord'}: the agency earns its commission on it, and any refund is deducted from the landlord's pay.`}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    {selectedTenantObj.property?.landlord?.name || 'This landlord'} has not said who holds security deposits.
                    Set &lsquo;Security deposits held by&rsquo; on the landlord before recording one.
                  </Alert>
                )}
              </Grid>
            )}
            {/* The period the payment covers, which replaced a plain "Number of Months".
                An advance is simply a period running further forward — three months paid
                up front records WHICH months were bought, not only how many. The number of
                months is worked out from these dates on the server. */}
            {createForm.paymentType === 'rent' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Period From"
                    InputLabelProps={{ shrink: true }}
                    value={createForm.periodStart}
                    onChange={e => setCreateForm({ ...createForm, periodStart: e.target.value })}
                    helperText="First day this payment covers"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Period To"
                    InputLabelProps={{ shrink: true }}
                    value={createForm.periodEnd}
                    onChange={e => setCreateForm({ ...createForm, periodEnd: e.target.value })}
                    inputProps={{ min: createForm.periodStart || undefined }}
                    error={!!createForm.periodEnd && !!createForm.periodStart && createForm.periodEnd < createForm.periodStart}
                    helperText="Last day covered — extend for an advance"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth type="date" label="Payment Date" InputLabelProps={{ shrink: true }} value={createForm.paymentDate} onChange={e => setCreateForm({ ...createForm, paymentDate: e.target.value })} helperText="When the money was received" />
            </Grid>
            {/* How the money arrived, and where it landed. Choosing a mode fills in its
                usual account; the account stays changeable for the odd payment that goes
                somewhere else. The journal entry debits whatever is selected here. */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                required
                label="Mode of Payment"
                value={createForm.paymentModeId}
                onChange={e => {
                  // Changing the mode changes which accounts are on offer, so any account
                  // already chosen is cleared rather than left pointing at the wrong kind.
                  const mode = paymentModes.find(m => m._id === e.target.value);
                  const matching = assetAccounts.filter(a => a.accountCategory === mode?.accountCategory);
                  setCreateForm({
                    ...createForm,
                    paymentModeId: e.target.value,
                    // Pre-select when there is only one candidate — the common case for an
                    // agency with a single bank account.
                    receivedIntoAccountId: matching.length === 1 ? (matching[0]._id || matching[0].id) : '',
                  });
                }}
                helperText={
                  paymentModes.length === 0
                    ? 'No modes set up — an admin can add them under Payment Modes'
                    : 'How the money reached you'
                }
              >
                {paymentModes.map(mode => (
                  <MenuItem key={mode._id} value={mode._id}>{mode.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                required
                label={selectedModeCategoryLabel ? `${selectedModeCategoryLabel} Account` : 'Received Into'}
                value={createForm.receivedIntoAccountId}
                onChange={e => setCreateForm({ ...createForm, receivedIntoAccountId: e.target.value })}
                disabled={!createForm.paymentModeId}
                helperText={
                  !createForm.paymentModeId
                    ? 'Choose a mode of payment first'
                    : accountsForSelectedMode.length === 0
                      ? `No ${selectedModeCategoryLabel} accounts set up — an admin can add one in the Chart of Accounts`
                      : 'Which account the money actually landed in'
                }
              >
                {accountsForSelectedMode.map(account => (
                  <MenuItem key={account._id || account.id} value={account._id || account.id}>
                    {account.code} — {account.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Status" value={createForm.status} onChange={e => setCreateForm({ ...createForm, status: e.target.value })}>
                <MenuItem value="received">Received (issue receipt)</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </TextField>
            </Grid>
            {!isSecurity && (
              <Grid item xs={12}>
                <LatePenaltyPanel
                  tenantId={createForm.tenant}
                  value={createPenalty}
                  onChange={setCreatePenalty}
                />
              </Grid>
            )}
          </Grid>
          <TextField fullWidth margin="normal" label="Staff Comments (optional)" multiline rows={2} value={createForm.staffComments} onChange={e => setCreateForm({ ...createForm, staffComments: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={depositBlocked}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog remains below (existing) */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Payment</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Tenant:</Typography>
                  <Typography>{selectedPayment.tenant?.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Amount Paid:</Typography>
                  <Typography>{formatCurrency(selectedPayment.amountPaid)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Rent Period:</Typography>
                  <Typography>{formatPeriod(selectedPayment)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Payment Date:</Typography>
                  <Typography>
                    {format(new Date(selectedPayment.paymentDate), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                {selectedPayment.receiptFileName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Uploaded Receipt:</Typography>
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewUploadedReceiptFile(selectedPayment);
                      }}
                    >
                      {selectedPayment.receiptFileName}
                    </Link>
                  </Grid>
                )}
                {selectedPayment.tenantResponse && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2">Tenant Response:</Typography>
                      <Typography>{selectedPayment.tenantResponse}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          <TextField
            fullWidth
            select
            label="Status"
            margin="normal"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="received">Received (Verify & Issue Receipt)</MenuItem>
            <MenuItem value="queried">Query Payment</MenuItem>
            <MenuItem value="rejected">Reject</MenuItem>
          </TextField>
          {formData.status === 'queried' && (
            <TextField
              fullWidth
              label="Comments for Tenant"
              margin="normal"
              multiline
              rows={4}
              value={formData.staffComments}
              onChange={(e) =>
                setFormData({ ...formData, staffComments: e.target.value })
              }
              placeholder="Explain why the payment is being queried..."
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentManagement;

